import type { ReactElement, FormEvent, ChangeEvent } from "react";
import type { User } from "@/types/user";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios, { AxiosError } from "axios";
import {
  MessageSquare,
  Send,
  CheckCircle,
  AlertCircle,
  ChevronLeft,
  Loader2,
  Mail,
  Clock,
  HelpCircle,
} from "lucide-react";
import PageLayout from "../components/PageLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Contact form data
 */
interface ContactFormData {
  name: string;
  email: string;
  category: string;
  subject: string;
  message: string;
}

/**
 * Category option
 */
interface CategoryOption {
  value: string;
  label: string;
}

/**
 * Contact response from API
 */
interface ContactResponse {
  ticket_id: string;
}

/**
 * API error response
 */
interface ApiErrorResponse {
  detail?: string;
}

const CATEGORIES: CategoryOption[] = [
  { value: "general", label: "General Question" },
  { value: "account", label: "Account & Billing" },
  { value: "technical", label: "Technical Issue" },
  { value: "feature", label: "Feature Request" },
  { value: "report", label: "Report a Problem" },
  { value: "other", label: "Other" },
];

/**
 * ContactPage Component
 *
 * Support contact form with category selection.
 */
const ContactPage = (): ReactElement => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(() => {
    try {
      const cached = localStorage.getItem("cached_user");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [ticketId, setTicketId] = useState<string>("");
  const [error, setError] = useState<string>("");

  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    category: "general",
    subject: "",
    message: "",
  });

  useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      try {
        const response = await axios.get<User>(`${API}/auth/me`, {
          withCredentials: true,
        });
        setUser(response.data);
        setFormData((prev) => ({
          ...prev,
          name: response.data.name || "",
          email: response.data.email || "",
        }));
      } catch (error) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError("");

    // Validation
    if (!formData.name.trim()) {
      setError("Please enter your name");
      return;
    }
    if (!formData.email.trim() || !formData.email.includes("@")) {
      setError("Please enter a valid email address");
      return;
    }
    if (formData.subject.length < 5) {
      setError("Subject must be at least 5 characters");
      return;
    }
    if (formData.subject.length > 200) {
      setError("Subject must be less than 200 characters");
      return;
    }
    if (formData.message.length < 20) {
      setError("Message must be at least 20 characters");
      return;
    }
    if (formData.message.length > 5000) {
      setError("Message must be less than 5000 characters");
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post<ContactResponse>(
        `${API}/v1/support/contact`,
        formData,
        { withCredentials: true }
      );

      setTicketId(response.data.ticket_id);
      setSuccess(true);
    } catch (err) {
      const axiosError = err as AxiosError<ApiErrorResponse>;
      if (axiosError.response?.status === 429) {
        setError(
          "You've submitted too many requests. Please wait a while before trying again."
        );
      } else if (axiosError.response?.data?.detail) {
        setError(axiosError.response.data.detail);
      } else {
        setError("Something went wrong. Please try again later.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    fontFamily: "Quicksand, sans-serif",
    color: "var(--st-text-heading)",
    border: "1px solid var(--st-border-light)",
    background: "var(--st-bg-surface)",
  };

  // Success State
  if (success) {
    return (
      <PageLayout>
        <div className="min-h-screen pb-16" style={{ background: "var(--st-bg-page)" }}>
          <div className="max-w-xl mx-auto px-4 pt-16">
            <div
              className="rounded-2xl p-8 text-center"
              style={{
                background: "var(--st-bg-surface)",
                border: "1px solid var(--st-border-light)",
                boxShadow: "0 4px 12px rgba(92, 64, 51, 0.06)",
              }}
            >
              <div
                className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                style={{ background: "#DCFCE7" }}
              >
                <CheckCircle className="w-8 h-8" style={{ color: "#22C55E" }} />
              </div>
              <h1
                className="text-2xl font-bold mb-2"
                style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
              >
                Message Sent!
              </h1>
              <p
                className="text-sm mb-4"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-muted)",
                }}
              >
                Thank you for reaching out. We've received your message and will
                respond within 24-48 hours.
              </p>
              <div
                className="inline-block px-4 py-3 rounded-xl mb-6"
                style={{ background: "var(--st-bg-page)", border: "1px solid var(--st-border-light)" }}
              >
                <p
                  className="text-xs mb-1"
                  style={{
                    fontFamily: "Quicksand, sans-serif",
                    color: "var(--st-text-muted)",
                  }}
                >
                  Your ticket reference:
                </p>
                <p
                  className="font-bold text-lg"
                  style={{
                    fontFamily: "Quicksand, sans-serif",
                    color: "var(--st-text-heading)",
                  }}
                >
                  {ticketId}
                </p>
              </div>
              <p
                className="text-xs mb-6"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-muted)",
                }}
              >
                Please save this reference number for future correspondence.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => navigate("/home")}
                  className="px-6 py-3 rounded-xl font-bold text-sm transition-all hover:scale-[1.02]"
                  style={{
                    background:
                      "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
                    color: "white",
                    fontFamily: "Quicksand, sans-serif"
                  }}
                >
                  Back to Home
                </button>
                <button
                  onClick={() => navigate("/help")}
                  className="px-6 py-3 rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] dark:hover:bg-gray-700"
                  style={{
                    background: "var(--st-bg-surface)",
                    border: "1px solid var(--st-border-light)",
                    color: "var(--st-text-heading)",
                    fontFamily: "Quicksand, sans-serif",
                  }}
                >
                  Browse FAQ
                </button>
              </div>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="min-h-screen pb-16" style={{ background: "var(--st-bg-page)" }}>
        {/* Back Button */}
        <div className="max-w-xl mx-auto px-4 pt-8">
          <button
            onClick={() => navigate("/help")}
            className="flex items-center gap-1 text-sm mb-4 transition-all hover:opacity-70"
            style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Help
          </button>
        </div>

        {/* Page Header Card */}
        <div className="max-w-xl mx-auto px-4">
          <div
            className="rounded-2xl p-6 mb-6"
            style={{
              background: "var(--st-bg-surface)",
              border: "1px solid var(--st-border-light)",
              boxShadow: "0 4px 12px rgba(92, 64, 51, 0.06)",
            }}
          >
            <div className="flex flex-col items-center text-center">
              <div
                className="w-16 h-16 mb-4 rounded-2xl flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
                }}
              >
                <MessageSquare className="w-8 h-8 text-white" />
              </div>
              <h1
                className="text-2xl md:text-3xl font-bold mb-2"
                style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
              >
                Contact Support
              </h1>
              <p
                className="text-sm max-w-md"
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
              >
                We're here to help! Fill out the form below and we'll get back
                to you within 24-48 hours.
              </p>

              {/* Info badges */}
              <div className="flex flex-wrap justify-center gap-3 mt-4">
                <div
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs"
                  style={{
                    background: "var(--st-bg-page)",
                    border: "1px solid var(--st-border-light)",
                    fontFamily: "Quicksand, sans-serif",
                    color: "var(--st-text-muted)",
                  }}
                >
                  <Clock className="w-3.5 h-3.5" />
                  24-48hr response
                </div>
                <div
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs"
                  style={{
                    background: "var(--st-bg-page)",
                    border: "1px solid var(--st-border-light)",
                    fontFamily: "Quicksand, sans-serif",
                    color: "var(--st-text-muted)",
                  }}
                >
                  <Mail className="w-3.5 h-3.5" />
                  Email confirmation
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Tip */}
        <div className="max-w-xl mx-auto px-4 mb-6">
          <button
            onClick={() => navigate("/help")}
            className="w-full flex items-center gap-3 p-4 rounded-xl transition-all hover:scale-[1.01]"
            style={{
              background: "var(--st-bg-page)",
              border: "1px solid var(--st-border-light)",
            }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: "#E0E7FF" }}
            >
              <HelpCircle className="w-5 h-5" style={{ color: "#6366F1" }} />
            </div>
            <div className="text-left">
              <p
                className="text-sm font-semibold"
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
              >
                Check our FAQ first
              </p>
              <p
                className="text-xs"
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
              >
                You might find an instant answer to your question
              </p>
            </div>
          </button>
        </div>

        {/* Contact Form */}
        <div className="max-w-xl mx-auto px-4">
          <form
            onSubmit={handleSubmit}
            className="rounded-xl p-6"
            style={{
              background: "var(--st-bg-surface)",
              border: "1px solid var(--st-border-light)",
              boxShadow: "0 4px 12px rgba(92, 64, 51, 0.06)",
            }}
          >
            {/* Name & Email Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              {/* Name */}
              <div>
                <label
                  className="block text-sm font-semibold mb-1.5"
                  style={{
                    fontFamily: "Quicksand, sans-serif",
                    color: "var(--st-text-heading)",
                  }}
                >
                  Your Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your name"
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-orange-200 transition-all"
                  style={inputStyle}
                  maxLength={100}
                />
              </div>

              {/* Email */}
              <div>
                <label
                  className="block text-sm font-semibold mb-1.5"
                  style={{
                    fontFamily: "Quicksand, sans-serif",
                    color: "var(--st-text-heading)",
                  }}
                >
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-orange-200 transition-all"
                  style={inputStyle}
                  maxLength={100}
                />
              </div>
            </div>

            {/* Category */}
            <div className="mb-4">
              <label
                className="block text-sm font-semibold mb-1.5"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-heading)",
                }}
              >
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-orange-200 transition-all cursor-pointer"
                style={inputStyle}
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Subject */}
            <div className="mb-4">
              <label
                className="block text-sm font-semibold mb-1.5"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-heading)",
                }}
              >
                Subject
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="Brief summary of your inquiry"
                className="w-full px-4 py-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-orange-200 transition-all"
                style={inputStyle}
                maxLength={200}
              />
              <p
                className="text-xs mt-1 text-right"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-muted)",
                }}
              >
                {formData.subject.length}/200
              </p>
            </div>

            {/* Message */}
            <div className="mb-6">
              <label
                className="block text-sm font-semibold mb-1.5"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-heading)",
                }}
              >
                Message
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Please describe your question or issue in detail..."
                rows={6}
                className="w-full px-4 py-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-orange-200 transition-all resize-none"
                style={inputStyle}
                maxLength={5000}
              />
              <p
                className="text-xs mt-1 text-right"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-muted)",
                }}
              >
                {formData.message.length}/5000 (min 20)
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div
                className="flex items-center gap-2 p-3 rounded-xl mb-4"
                style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}
              >
                <AlertCircle
                  className="w-4 h-4 flex-shrink-0"
                  style={{ color: "#EF4444" }}
                />
                <p
                  className="text-sm"
                  style={{
                    fontFamily: "Quicksand, sans-serif",
                    color: "#EF4444",
                  }}
                >
                  {error}
                </p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3.5 rounded-xl font-bold text-sm transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background:
                  "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
                color: "white",
                fontFamily: "Quicksand, sans-serif"
              }}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Message
                </>
              )}
            </button>
          </form>

          {/* Privacy Note */}
          <div className="mt-4 text-center">
            <p
              className="text-xs"
              style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
            >
              By submitting this form, you agree to our{" "}
              <button
                onClick={() => navigate("/privacy")}
                className="underline hover:opacity-70"
              >
                Privacy Policy
              </button>
            </p>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default ContactPage;
